import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { AddFamilyMemberBody } from '@/features/families/api';
import type { FamilyMemberViewModel } from '@/models/family';

import { AddMemberLinkExistingPanel } from './AddMemberLinkExistingPanel';
import { AddMemberNewPersonPanel } from './AddMemberNewPersonPanel';

type Mode = 'link' | 'new';

interface AddMemberDialogProps {
  readonly busy: boolean;
  readonly existingMembers: readonly FamilyMemberViewModel[];
  readonly onCancel: () => void;
  readonly onSubmit: (body: AddFamilyMemberBody) => void;
}

/**
 * Modal for adding a family member. Two modes:
 *   - "link" — search live patient records and link an existing person.
 *   - "new"  — capture first/last name + role for an unregistered relative.
 */
export function AddMemberDialog({
  busy,
  existingMembers,
  onCancel,
  onSubmit,
}: AddMemberDialogProps): ReactElement {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('link');

  const excludePersonGuids = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        existingMembers
          .map((m) => m.personGuid)
          .filter((g): g is string => g !== null),
      ),
    [existingMembers],
  );

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <DialogContent className="max-w-lg" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.family.members.addDialog.title')}</DialogTitle>
          <DialogDescription>
            {t(
              mode === 'link'
                ? 'routes.family.members.addDialog.descriptionLink'
                : 'routes.family.members.addDialog.descriptionNew',
            )}
          </DialogDescription>
        </DialogHeader>

        <ModeSwitch mode={mode} onChange={setMode} disabled={busy} />

        {mode === 'link' ? (
          <AddMemberLinkExistingPanel
            busy={busy}
            excludePersonGuids={excludePersonGuids}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        ) : (
          <AddMemberNewPersonPanel busy={busy} onCancel={onCancel} onSubmit={onSubmit} />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ModeSwitchProps {
  readonly mode: Mode;
  readonly onChange: (next: Mode) => void;
  readonly disabled: boolean;
}

function ModeSwitch({ mode, onChange, disabled }: ModeSwitchProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      role="tablist"
      aria-label={t('routes.family.members.addDialog.modeSwitchAria')}
      className="inline-flex rounded-md border border-border bg-muted/40 p-0.5"
    >
      <ModeButton
        value="link"
        active={mode === 'link'}
        disabled={disabled}
        onClick={() => {
          onChange('link');
        }}
        label={t('routes.family.members.addDialog.modeLink')}
      />
      <ModeButton
        value="new"
        active={mode === 'new'}
        disabled={disabled}
        onClick={() => {
          onChange('new');
        }}
        label={t('routes.family.members.addDialog.modeNew')}
      />
    </div>
  );
}

interface ModeButtonProps {
  readonly value: Mode;
  readonly active: boolean;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly label: string;
}

function ModeButton({ value, active, disabled, onClick, label }: ModeButtonProps): ReactElement {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-mode={value}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}
